import { PropsWithChildren, ReactElement } from 'react';
import { get, noop } from 'lodash';

import classNames from 'classnames';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';

import { Icon, IconName } from '../Icon';
import { IconSvgName, ICON_SVG } from '../IconSvg';

import { ButtonSocial } from './ButtonSocial/ButtonSocial';
import { ButtonVariant, ButtonType, CommonButtonProps } from './Button.types';
import {
  BUTTON_TYPE_FILL,
  BUTTON_SIZE_MAP,
  ICON_SVG_SIZE_MAP,
} from './Button.const';

import styles from './Button.module.scss';

export type ButtonProps = PropsWithChildren<
  {
    block?: boolean;
    type?: ButtonType;
    variant?: ButtonVariant;
    outset?: boolean;
    icon?: IconName;
    href?: string;
    iconSvg?: IconSvgName;
  } & CommonButtonProps
>;

interface ButtonComponent {
  (props: ButtonProps): ReactElement;
  Social: typeof ButtonSocial;
}

export const Button: ButtonComponent = ({
  id,
  icon,
  iconSvg,
  outset,
  loading,
  children,
  disabled,
  className,
  size = 'm',
  block = true,
  type = 'button',
  onClick = noop,
  variant = 'primary',
  href,
}) => {
  return (
    <IonButton
      id={id}
      type={type}
      disabled={disabled}
      href={href}
      className={classNames(
        styles.root,
        styles[variant],
        {
          [styles.block]: block,
          [styles.small]: size === 's',
          [styles.extraSmall]: size === 'xs',
          [styles.large]: size === 'lg',
          [styles.outset]: outset,
          [styles.withIcon]: !!icon,
          [styles.withIconOnly]: !!icon && !children,
        },
        className
      )}
      size={BUTTON_SIZE_MAP[size]}
      onClick={!loading && onClick}
      fill={BUTTON_TYPE_FILL[variant]}
      expand={block ? 'block' : undefined}
    >
      <span
        className={classNames(styles.label, styles[variant], {
          [styles.loading]: loading,
          [styles.small]: size === 's',
          [styles.extraSmall]: size === 'xs',
        })}
      >
        {!!icon && <Icon name={icon} />}
        {children}
      </span>
      {!!iconSvg && (
        <IonIcon
          slot="start"
          className={classNames(styles.iconSvg, {
            [styles.loading]: loading,
          })}
          icon={get(ICON_SVG, iconSvg)}
          style={{ fontSize: ICON_SVG_SIZE_MAP[size] }}
        />
      )}
      {loading && (
        <IonSpinner
          name="crescent"
          className={classNames(styles.spinner, {
            [styles.extraSmall]: size === 'xs',
          })}
        />
      )}
    </IonButton>
  );
};

Button.Social = ButtonSocial;
